import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { AnimatedOutlet } from "./Motion";
import { PortalParticipantsProvider } from "../context/PortalParticipantsContext";
import { PortalChromeContext } from "../context/PortalChromeContext";
import { Home, Calendar, LogOut, ArrowLeftRight, Bell, FileUp } from "lucide-react";
import { migrateLegacyPortalPath, portalSectionFromPath } from "../lib/portalPaths";
import { cn } from "../lib/utils";
import { PortalMobileTopNav } from "./PortalMobileTopNav";

/** Client workspace — Home only (progress, shared files, next meeting). */
const NAV_CLIENT = [
  { to: "", key: "home", label: "Home", icon: Home, end: true, testid: "nav-home" },
];

/**
 * Admin engagement workspace (not /admin Home).
 * Home includes the project timeline; Timeline tab removed.
 */
const NAV_ADMIN = [
  { to: "", key: "home", label: "Home", icon: Home, end: true, testid: "nav-home" },
  { to: "files", key: "files", label: "Library", icon: FileUp, testid: "nav-library" },
  { to: "calendar", key: "calendar", label: "Meetings", icon: Calendar, testid: "nav-meetings" },
];

function notificationTitleText(notification) {
  const title = typeof notification?.title === "string" ? notification.title : "";
  if (notification?.type !== "new_message") return title;
  const maxLen = 96;
  if (title.length <= maxLen) return title;
  return `${title.slice(0, maxLen - 3).trimEnd()}...`;
}

function PortalBrandMark({ isAdmin, className, style }) {
  if (isAdmin) {
    return (
      <NavLink
        to="/admin"
        end
        className={cn(className, "hover:text-[var(--t-accent)] transition-colors")}
        style={style}
        data-testid="portal-admin-link"
      >
        TAĪSTU
      </NavLink>
    );
  }
  return (
    <div className={className} style={style}>
      TAĪSTU
    </div>
  );
}

export const PortalLayout = () => {
  const { user, client, logout, switchClient } = useAuth();
  const [composerActive, setComposerActive] = useState(false);
  const sidebarFooterRef = useRef(null);
  const mainScrollRef = useRef(null);
  const mobileChromeRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const el = sidebarFooterRef.current;
    if (!el) return undefined;

    const setVar = () => {
      const h = Math.round(el.getBoundingClientRect().height || 0);
      document.documentElement.style.setProperty("--portal-sidebar-footer-h", `${h}px`);
    };

    setVar();
    const ro = new ResizeObserver(() => setVar());
    ro.observe(el);
    window.addEventListener("resize", setVar);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVar);
    };
  }, [user?.role]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Bottom dock removed — keep var at 0 for pages that still subtract it.
    document.documentElement.style.setProperty("--portal-mobile-nav-h", "0px");
    const setHeader = () => {
      const h = mobileChromeRef.current?.getBoundingClientRect().height ?? 0;
      document.documentElement.style.setProperty("--portal-mobile-header-h", `${Math.round(h)}px`);
    };
    setHeader();
    const ro = new ResizeObserver(setHeader);
    if (mobileChromeRef.current) ro.observe(mobileChromeRef.current);
    window.addEventListener("resize", setHeader);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setHeader);
    };
  }, [user?.role, composerActive, location.pathname]);

  const goToAdmin = () => {
    switchClient(null);
    navigate("/admin");
  };

  const isAdmin = user?.role === "admin";
  const visibleNav = isAdmin ? NAV_ADMIN : NAV_CLIENT;

  const section = portalSectionFromPath(location.pathname);
  const footerAlignedRoute =
    section === "messages" || (section === "calendar" && !isAdmin);
  const hideMobileNav = composerActive && section === "messages";
  const setComposerActiveStable = useCallback((active) => setComposerActive(Boolean(active)), []);

  useEffect(() => {
    if (section !== "messages") setComposerActive(false);
  }, [section]);

  return (
    <PortalParticipantsProvider>
    <PortalChromeContext.Provider value={{ composerActive, setComposerActive: setComposerActiveStable }}>
    <div
      className="App glass-neo-lab relative flex min-h-screen flex-col max-w-full min-w-0 overflow-x-clip lg:h-dvh lg:max-h-dvh lg:flex-row lg:overflow-hidden"
      data-neo="signal"
      data-testid="portal-layout"
    >
      <div className="glass-neo-lab__wash" aria-hidden />

      <header
        ref={mobileChromeRef}
        className={cn(
          "relative z-10 flex min-w-0 shrink-0 flex-col lg:hidden",
          hideMobileNav && "hidden",
        )}
        data-testid="mobile-portal-header"
        aria-hidden={hideMobileNav}
      >
        <div className="flex items-center justify-between gap-3 px-4 pt-3 sm:px-5">
          <div className="min-w-0 flex-1 leading-tight">
            <PortalBrandMark
              isAdmin={isAdmin}
              className="font-heading truncate text-base leading-tight tracking-tight text-[var(--t-text)]"
            />
            <div className="t-meta truncate text-xs leading-tight" data-testid="client-context-label-mobile">
              {client?.name || "—"}
            </div>
          </div>
          <NotificationBell testid="notification-bell-mobile" />
        </div>
        <PortalMobileTopNav
          items={visibleNav}
          isAdmin={isAdmin}
          onStudio={goToAdmin}
          onLogout={logout}
        />
      </header>

      <aside
        className="neo-shell relative z-10 hidden flex-shrink-0 flex-col overflow-hidden lg:sticky lg:top-3 lg:m-3 lg:mr-0 lg:flex lg:h-[calc(100dvh-1.5rem)] lg:w-64 xl:w-72"
        data-testid="sidebar"
      >
        <div className="flex items-start justify-between gap-3 px-6 pb-2 pt-7">
          <div className="min-w-0">
            <PortalBrandMark
              isAdmin={isAdmin}
              className="font-heading text-xl tracking-tight text-[var(--t-text)]"
              style={{ letterSpacing: "0.04em" }}
            />
            <div className="t-meta mt-2 truncate text-sm" data-testid="client-context-label">
              {client?.name || "—"}
            </div>
          </div>
          <NotificationBell testid="notification-bell-sidebar" />
        </div>

        <nav className="flex-1 py-3" data-testid="portal-nav">
          {visibleNav.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `neo-nav-item ${isActive ? "is-active" : ""}`}
              data-testid={testid}
            >
              <Icon size={16} strokeWidth={1.4} />
              <span className="leading-snug">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div
          ref={sidebarFooterRef}
          className="mt-auto border-t border-white/40 px-5 py-6"
          data-testid="sidebar-footer"
        >
          {user?.role === "admin" && (
            <button
              type="button"
              onClick={goToAdmin}
              className="t-btn-ghost mb-3 flex w-full min-h-[44px] items-center justify-start gap-2 sm:w-auto sm:min-h-0"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeftRight size={14} strokeWidth={1.4} /> Studio
            </button>
          )}
          {user?.role !== "admin" && (
            <div className="t-meta mb-2 break-words text-sm" data-testid="signed-in-as">{user?.name}</div>
          )}
          <button
            type="button"
            onClick={logout}
            className="t-btn-ghost flex w-full min-h-[44px] items-center justify-start gap-2 sm:w-auto sm:min-h-0"
            data-testid="logout-btn"
          >
            <LogOut size={14} strokeWidth={1.4} /> Sign out
          </button>
        </div>
      </aside>

      <main
        className={cn(
          "neo-shell relative z-10 m-3 flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-x-clip lg:ml-2",
          footerAlignedRoute ? "lg:pb-0" : "lg:pb-3",
          "pb-3",
        )}
      >
        <div
          ref={mainScrollRef}
          className={`relative flex min-h-0 w-full max-w-full flex-1 flex-col lg:overscroll-y-contain ${
            footerAlignedRoute ? "lg:overflow-hidden" : "lg:overflow-y-auto"
          }`}
        >
          <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col xl:mx-auto xl:max-w-[1200px]">
            <AnimatedOutlet scrollRootRef={mainScrollRef} />
          </div>
        </div>
      </main>
    </div>
    </PortalChromeContext.Provider>
    </PortalParticipantsProvider>
  );
};

export const NotificationBell = ({ testid = "notification-bell", dock = false }) => {
  const { client } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const markRead = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    if (n.link) navigate(client ? migrateLegacyPortalPath(n.link, client) : n.link);
    setOpen(false);
    load();
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const el = btnRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const gutter = 12;
      const panelW = Math.min(340, vw - gutter * 2);

      const maxPanelH = Math.min(480, Math.max(160, Math.round(vh * 0.7)));

      const measuredH = Math.ceil(panelRef.current?.getBoundingClientRect()?.height || 0) || maxPanelH;
      const panelH = Math.min(maxPanelH, measuredH);

      let left = r.right - panelW;
      if (left < gutter) {
        left = Math.max(gutter, Math.min(r.left, vw - panelW - gutter));
      }
      left = Math.max(gutter, Math.min(left, vw - panelW - gutter));

      const preferredTop = r.bottom + 8;
      const maxTop = vh - gutter - panelH;
      const top = Math.max(gutter, Math.min(preferredTop, maxTop));

      setPos({ top, left });
    };
    update();
    requestAnimationFrame(update);
    const ro = panelRef.current ? new ResizeObserver(() => update()) : null;
    if (panelRef.current) ro?.observe(panelRef.current);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, items.length, unread]);

  return (
    <div className={cn("relative", dock && "flex items-center justify-center")}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "relative text-[var(--t-meta)] transition-colors hover:text-[var(--t-accent)]",
          dock ? "inline-flex h-8 w-8 items-center justify-center" : "rounded-xl p-2",
        )}
        aria-label="Notifications"
        data-testid={testid || "notification-bell"}
      >
        <Bell size={dock ? 18 : 16} strokeWidth={1.4} />
        {unread > 0 && (
          <span
            className={cn(
              "absolute rounded-full bg-[var(--t-accent)]",
              dock ? "right-0.5 top-0.5 h-1.5 w-1.5" : "right-1 top-1 h-2 w-2",
            )}
            data-testid="notification-badge"
          />
        )}
      </button>
      {open && typeof document !== "undefined" && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div
            ref={panelRef}
            className="neo-panel fixed z-[70] w-[min(340px,calc(100vw-24px))] max-h-[min(480px,70vh)] overflow-y-auto"
            style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999, visibility: "hidden" }}
            data-testid="notification-panel"
          >
            <div className="flex items-center justify-between border-b border-white/40 px-4 py-3">
              <div className="label-eyebrow">Notifications</div>
              {unread > 0 && (
                <button onClick={markAll} className="t-btn-ghost text-xs" data-testid="mark-all-read-btn">
                  Mark all read
                </button>
              )}
            </div>
            {items.length === 0 ? (
              <div className="p-8 text-center t-meta" data-testid="notification-empty">All caught up.</div>
            ) : items.slice(0, 30).map((n) => (
              <button
                key={n.id}
                onClick={() => markRead(n)}
                className={`block w-full border-b border-black/5 px-4 py-3 text-left last:border-b-0 t-row-hover ${
                  !n.read ? "bg-[rgba(0,30,255,0.025)]" : ""
                }`}
                data-testid={`notification-${n.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className={`truncate text-sm leading-tight ${!n.read ? "text-[var(--t-text)]" : "text-[var(--t-meta)]"}`}>
                      {notificationTitleText(n)}
                    </div>
                    <div className="t-meta mt-1 text-xs">
                      {new Date(n.created_at).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  {!n.read && <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--t-accent)]" />}
                </div>
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};
